import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authenticate, getStoredToken, setAuthToken } from '../api/client';

interface AuthContextValue {
  ready: boolean;
  error: string | null;
  token: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  ready: false,
  error: null,
  token: null,
});

function getInitData(): string | null {
  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    return tg.initData;
  }

  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) {
    return import.meta.env.VITE_DEV_INIT_DATA;
  }

  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken());

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const stored = getStoredToken();
      if (stored) {
        setAuthToken(stored);
        if (!cancelled) {
          setToken(stored);
          setReady(true);
        }
        return;
      }

      const initData = getInitData();
      if (!initData) {
        if (!cancelled) {
          setError('Не удалось получить данные авторизации Telegram');
          setReady(true);
        }
        return;
      }

      try {
        const result = await authenticate(initData);
        setAuthToken(result.token);
        if (!cancelled) {
          setToken(result.token);
          setReady(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Ошибка авторизации');
          setReady(true);
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ready, error, token }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        expand: () => void;
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
          showProgress: () => void;
          hideProgress: () => void;
        };
        HapticFeedback?: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
        };
        themeParams: Record<string, string>;
        colorScheme: 'light' | 'dark';
      };
    };
  }
}
